import { JsonProperty, JsonObject } from 'typescript-json-serializer';
import { ClimbingRoute } from '../climbing/ClimbingRoute';
import type { GymFeedbackSettings } from '../climbing/RouteFeedback';

/**
 * Gym model representing a climbing gym
 */
@JsonObject()
export class Gym {
  @JsonProperty()
  id: string;

  @JsonProperty()
  name?: string;

  @JsonProperty()
  imageUrl?: string;

  @JsonProperty()
  logoUrl?: string;
  
  @JsonProperty()
  styles?: string[];
  
  @JsonProperty()
  climbing_routes?: Record<string, any>; // Map of route IDs to route data
  
  @JsonProperty()
  feedbackSettings?: GymFeedbackSettings; // Route feedback settings

  constructor(
    id: string,
    options: {
      name?: string;
      imageUrl?: string;
      logoUrl?: string;
      styles?: string[];
      climbing_routes?: Record<string, any>;
      feedbackSettings?: GymFeedbackSettings;
    } = {}
  ) {
    this.id = id;
    this.name = options.name || undefined;
    this.imageUrl = options.imageUrl || undefined;
    this.logoUrl = options.logoUrl || undefined;
    this.styles = options.styles || [];
    this.climbing_routes = options.climbing_routes || {};
    this.feedbackSettings = options.feedbackSettings || {
      enabled: true,
      allowStars: true,
      allowGradeSuggestions: true,
      allowComments: true,
      requireClimbToFeedback: false,
      moderateComments: false
    };
  }

  // Method to convert to Firestore data
  toFirestore() {
    // Create a clean object with no undefined values
    const cleanData: Record<string, any> = {
      id: this.id,
      name: this.name || null,
      imageUrl: this.imageUrl || null,
      logoUrl: this.logoUrl || null,
      styles: this.styles || [],
      climbing_routes: this.climbing_routes || {}
    };
    
    return cleanData;
  }

  // Static method to create Gym from Firestore data
  static fromFirestore(data: any): Gym {
    return new Gym(data.id, {
      name: data.name,
      imageUrl: data.imageUrl,
      logoUrl: data.logoUrl,
      styles: data.styles || [],
      climbing_routes: data.climbing_routes || {}
    });
  }
  
  // Add a climbing route to this gym
  addClimbingRoute(route: ClimbingRoute): void {
    if (!this.climbing_routes) {
      this.climbing_routes = {};
    }
    
    // Get the route data but ensure we handle undefined values
    const routeData = route.toFirestore();
    
    // Store the route data in the climbing_routes map
    this.climbing_routes[route.id] = routeData;
    
  }
  
  // Get all climbing routes for this gym
  getClimbingRoutes(): ClimbingRoute[] {
    if (!this.climbing_routes) {
      return [];
    }
    
    return Object.entries(this.climbing_routes).map(([routeId, routeData]) => {
      return ClimbingRoute.fromFirestore({ ...routeData, id: routeId });
    });
  }
}
